import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SortByAlphaIcon from "@mui/icons-material/SortByAlpha";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Typography,
  Tooltip,
  Chip,
  Button,
  Pagination,
  Box,
  CircularProgress,
} from "@mui/material";
import styled from "@emotion/styled";
import { useMutation, useQuery, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  getWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { ListBox, StyledTypography } from "../../../styles/commonStyles";
import { IconColor } from "../../../styles/constants";
import { successToast, errorToast } from "../../../utils/helper";
import { CommonSearch } from "../../shared/CommonSearch";
import { useUserRole } from "../../../hooks/useUserRole";

const SectionContainer = styled(Box)(({ theme, disabled }) => ({
  borderRadius: 14,
  boxSizing: "border-box",
  backgroundColor: disabled ? "#f5f5f5" : "white",
  opacity: disabled ? 0.7 : 1,
  pointerEvents: disabled ? "none" : "auto",
  cursor: disabled ? "not-allowed" : "auto",
  filter: disabled ? "blur(5px)" : "none",
  transition: "filter 0.3s ease",
  position: "relative",
  "&::after": disabled
    ? {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        cursor: "not-allowed",
        zIndex: 5,
        borderRadius: 14,
      }
    : {},
}));

const CompanyListBox = styled(ListBox)(
  ({ hasdata, isdisabled, iscurrent }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingLeft: "20px",
    paddingRight: "10px",
    opacity: isdisabled ? 0.5 : 1,
    cursor: isdisabled ? "not-allowed" : "pointer",
    backgroundColor: iscurrent
      ? "#e3f2fd"
      : hasdata
        ? "#f1f8e9"
        : "transparent",
    "&:hover": {
      backgroundColor: isdisabled
        ? iscurrent
          ? "#e3f2fd"
          : hasdata
            ? "#f1f8e9"
            : "transparent"
        : iscurrent
          ? "#bbdefb"
          : "#f5f5f5",
    },
  }),
);

const StatusChip = styled(Chip)(({ chiptype }) => ({
  fontSize: 10,
  height: 20,
  minWidth: 60,
  backgroundColor:
    chiptype === "hasdata"
      ? "#c8e6c9"
      : chiptype === "current"
        ? "#e3f2fd"
        : "#e8f5e9",
  color:
    chiptype === "hasdata"
      ? "#1b5e20"
      : chiptype === "current"
        ? "#1565c0"
        : "#2e7d32",
  fontWeight: 500,
  marginLeft: 8,
}));

export const CategoryUser = ({
  companyForm,
  setCompanyForm,
  companyData,
  isCompanyFetching,
  addNewCategory,
  setAddNewCategory,
  setCategoryData,
  categoryData,
  selectedCategories,
  companyError,
  isTenantBlocked,
  tenantMessage,
  onCompanySelect,
  isRightPaneDisabled,
  hasUnsavedChanges,
  copySuccessTrigger,
  submitCopyCategories,
  originalCompaniesWithCategory,
  setOriginalCompaniesWithCategory,
  companyPaginationModel,
  setCompanyPaginationModel,
}) => {
  const [searchUser, setSearchUser] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [userPaginationModel, setUserPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [accumulatedUsers, setAccumulatedUsers] = useState([]);
  const [accumulatedCompanies, setAccumulatedCompanies] = useState([]);
  const [companiesWithCategory, setCompaniesWithCategory] = useState({});
  const queryClient = useQueryClient();

  const lastCategoryIdRef = useRef(null);
  const lastCopyTriggerRef = useRef(0);

  const { currentCompany, tenantString } = useOutletContext();
  const { isCurrentUserSysAdmin } =
    useUserRole();

  const isPowerAdmin = isCurrentUserSysAdmin;

  // ✅ Check if company selection has changed
  const hasCompanyChanges = useMemo(() => {
    if (!categoryData?.categoryID) return false;

    const currentIds = companyForm?.map((c) => String(c.companyID)).sort() || [];
    const originalIds =
      originalCompaniesWithCategory?.map((c) => String(c.companyID)).sort() || [];

    if (currentIds.length !== originalIds.length) return true;
    return currentIds.some((id, index) => id !== originalIds[index]);
  }, [companyForm, originalCompaniesWithCategory, categoryData?.categoryID]);

  const handleCancelCompanies = () => {
    setCompanyForm(originalCompaniesWithCategory);
  };

  const filteredCompany = useMemo(() => {
    const list = accumulatedCompanies || [];

    // If super admin/tenant admin, show all
    if (isPowerAdmin) return list;

    // For Company Admin, only show current company or companies they are admin of
    // Since we don't have a full list of administered companies here easily,
    // we restrict it to the currentCompany (which they must be admin of to see this page properly)
    return list.filter((item) => item.companyID === currentCompany);
  }, [accumulatedCompanies, isPowerAdmin, currentCompany]);

  // Fetch which companies have this category and if they have data
  const { data: categoryCompanyData, refetch: refetchCategoryCompanies } =
    useQuery({
      queryKey: [
        "categoryCompanies",
        categoryData?.categoryID,
        tenantString,
        copySuccessTrigger,
      ],
      queryFn: () =>
        getWithQueryApiServices(
          apiRoutes.GET_CATEGORY_COMPANIES,
          `categoryID=${categoryData?.categoryID}${tenantString}`,
        ),
      enabled: !!categoryData?.categoryID,
    });

  // ✅ When category changes or copy succeeds - refetch
  useEffect(() => {
    const isAfterCopy =
      copySuccessTrigger > 0 &&
      copySuccessTrigger !== lastCopyTriggerRef.current;

    if (
      categoryData?.categoryID &&
      (categoryData?.categoryID !== lastCategoryIdRef.current || isAfterCopy)
    ) {
      refetchCategoryCompanies();
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
    } else if (!categoryData?.categoryID) {
      setCompaniesWithCategory({});
      setCompanyForm([]);
      setOriginalCompaniesWithCategory([]);
      lastCategoryIdRef.current = null;
    }
  }, [
    categoryData?.categoryID,
    copySuccessTrigger,
    setCompanyForm,
    setOriginalCompaniesWithCategory,
    refetchCategoryCompanies,
    queryClient,
  ]);

  // ✅ Process API response and pre-select
  useEffect(() => {
    const responseData = categoryCompanyData?.data;
    const companyList = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData?.data)
        ? responseData.data
        : null;

    if (companyList && companyList.length >= 0) {
      // Build a map: { companyID: { exists: true, hasData: true/false } }
      const companyMap = {};
      const originalCompanies = [];

      companyList.forEach((item) => {
        const hasData = item.hasData === 1 || item.hasData === true;
        companyMap[item.companyID] = {
          exists: true,
          hasData,
        };
        originalCompanies.push({
          companyID: item.companyID,
          hasData,
        });
      });

      setCompaniesWithCategory(companyMap);

      // ✅ Sync local selection with server results
      // (Do this if it's a new category selection OR if we just finished an Update)
      const isNewCategory =
        categoryData?.categoryID !== lastCategoryIdRef.current;
      const isAfterCopy =
        copySuccessTrigger > 0 &&
        copySuccessTrigger !== lastCopyTriggerRef.current;

      if (
        (isNewCategory || isAfterCopy) &&
        categoryData?.categoryID
      ) {
        setCompanyForm(originalCompanies);
      }

      // ✅ Store ALL original companies for tracking changes
      setOriginalCompaniesWithCategory(originalCompanies);

      // Update refs
      lastCategoryIdRef.current = categoryData?.categoryID;
      if (isAfterCopy) {
        lastCopyTriggerRef.current = copySuccessTrigger;
      }
    } else {
      setCompaniesWithCategory({});
      setOriginalCompaniesWithCategory([]);
    }
  }, [
    categoryCompanyData?.data,
    categoryData?.categoryID,
    filteredCompany,
    copySuccessTrigger,
    setCompanyForm,
    setOriginalCompaniesWithCategory,
  ]);

  // ✅ Check if company checkbox is disabled - ONLY if has data
  const isCompanyCheckboxDisabled = (companyID) => {
    if (isRightPaneDisabled || isTenantBlocked) return true;
    if (selectedCategories?.length < 1) return true;

    const companyInfo = companiesWithCategory[companyID];
    // ✅ Only disabled if has data - current company can still be unchecked if no data
    if (companyInfo?.exists && companyInfo?.hasData) {
      return true;
    }

    return false;
  };

  // Get status badge for company
  const getCompanyStatus = (companyID) => {
    const isCurrent = companyID === currentCompany;
    const companyInfo = companiesWithCategory[companyID];

    // ✅ Show both badges if applicable
    const badges = [];

    if (isCurrent) {
      badges.push(
        <Tooltip key="current" title="This is the currently selected company">
          <StatusChip chiptype="current" label="Current" size="small" />
        </Tooltip>,
      );
    }

    if (companyInfo?.hasData) {
      badges.push(
        <Tooltip
          key="hasdata"
          title="This company has files/data with this category and cannot be removed"
        >
          <StatusChip chiptype="hasdata" label="Already Exists" size="small" />
        </Tooltip>,
      );
    } else if (companyInfo?.exists && !isCurrent) {
      badges.push(
        <Tooltip
          key="exists"
          title="Category exists in this company"
        >
          <StatusChip chiptype="exists" label="Already Exists" size="small" />
        </Tooltip>,
      );
    } else if (companyInfo?.exists && isCurrent) {
      badges.push(
        <Tooltip
          key="exists"
          title="Category exists in this company"
        >
          <StatusChip chiptype="exists" label="Already Exists" size="small" />
        </Tooltip>,
      );
    }

    return badges.length > 0 ? (
      <div style={{ display: "flex", gap: 4 }}>{badges}</div>
    ) : null;
  };

  // Handle company checkbox toggle
  const handleCompanyCheckbox = (val) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (isRightPaneDisabled) {
      errorToast("Please complete the current operation first.");
      return;
    }

    if (selectedCategories?.length < 1) {
      errorToast("Please select a category first.");
      return;
    }

    const companyInfo = companiesWithCategory[val.companyID];
    const isCurrentlySelected = companyForm?.some(
      (item) => String(item?.companyID) === String(val?.companyID),
    );

    // ✅ Prevent unchecking if has data (applies to ALL companies including current)
    if (companyInfo?.hasData && isCurrentlySelected) {
      errorToast("Cannot remove category from company with existing data");
      return;
    }

    // Toggle company selection
    if (isCurrentlySelected) {
      setCompanyForm(
        companyForm.filter((item) => String(item?.companyID) !== String(val?.companyID)),
      );
    } else {
      setCompanyForm([...companyForm, val]);
    }
  };

  const checkUserExistForCategory = (data) => {
    const getData = usersData?.data?.userList?.find(
      (item) => item?.userID === data?.userID,
    );
    return getData?.categoryList?.includes(categoryData?.name);
  };

  const addUserForCategory = (user) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (isRightPaneDisabled) {
      errorToast("Please complete the current operation first.");
      return;
    }

    const workData = {
      ...user,
      categoryList: [
        { categoryID: categoryData?.categoryID, securityID: null },
      ],
    };
    addUserForCategorys.mutate(workData);
  };

  const removeUser = (user) => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (isRightPaneDisabled) {
      errorToast("Please complete the current operation first.");
      return;
    }

    const workData = {
      userId: user?.userID,
      companyId: currentCompany,
      categoryId: categoryData?.categoryID,
    };
    removeUserFromCategory.mutate(workData);
  };

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const { data: usersData, refetch: userDatas } = useQuery({
    queryKey: [
      "usersList",
      currentCompany,
      searchUser,
      tenantString,
      sortOrder,
    ],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_USERS_LIST,
        `pageNumber=1&pageSize=10000&selectedCompany=${currentCompany}&search=${searchUser}&sortOrder=${sortOrder}${tenantString}`,
      ),
    enabled: !!currentCompany,
  });

  const { data: allUsersList, isFetching: isAllUsersFetching } = useQuery({
    queryKey: ["allUsersList", searchUser, sortOrder, userPaginationModel, tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_USERS_LIST,
        `pageNumber=${userPaginationModel.page + 1}&pageSize=${userPaginationModel.pageSize}&search=${searchUser}&sortOrder=${sortOrder}${tenantString}`,
      ),
    placeholderData: keepPreviousData,
  });

  const lastUserScrollRef = useRef(0);
  const handleScroll = (e) => {
    const now = Date.now();
    if (now - lastUserScrollRef.current < 100) return;
    lastUserScrollRef.current = now;

    if (isAllUsersFetching) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      if (allUsersList?.data?.count !== undefined && accumulatedUsers.length >= allUsersList?.data?.count) {
        return;
      }
      setUserPaginationModel((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  useEffect(() => {
    setUserPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchUser, sortOrder, categoryData?.categoryID]);

  useEffect(() => {
    if (allUsersList?.data?.userList) {
      if (userPaginationModel.page === 0) {
        setAccumulatedUsers(allUsersList.data.userList);
      } else {
        setAccumulatedUsers((prev) => {
          const existingIds = new Set(prev.map((u) => u.userID));
          const newUsers = allUsersList.data.userList.filter((u) => !existingIds.has(u.userID));
          return [...prev, ...newUsers];
        });
      }
    }
  }, [allUsersList, userPaginationModel.page]);

  const addUserForCategorys = useMutation({
    mutationKey: ["addUserForCategory"],
    mutationFn: (workData) =>
      putWithQueryApiServices(
        apiRoutes.ADD_USER_FROM_CAT,
        `selectedCompany=${currentCompany}${tenantString}`,
        workData,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
      userDatas();
      successToast("User Added For Category");
    },
    onError: (error) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(error?.response?.data?.message || "Failed to add user");
      }
    },
  });

  const removeUserFromCategory = useMutation({
    mutationKey: ["updateCategoryform"],
    mutationFn: (workData) =>
      putWithQueryApiServices(
        apiRoutes.REMOVE_USER_FROM_CAT,
        `selectedCompany=${currentCompany}${tenantString}`,
        workData,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usersList"] });
      userDatas();
      successToast("User Removed From Category");
    },
    onError: (error) => {
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast(error?.response?.data?.message || "Failed to remove user");
      }
    },
  });

  const lastCompanyScrollRef = useRef(0);
  const handleCompanyScroll = (e) => {
    const now = Date.now();
    if (now - lastCompanyScrollRef.current < 100) return;
    lastCompanyScrollRef.current = now;

    if (isCompanyFetching) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 10) {
      if (companyData?.data?.count !== undefined && accumulatedCompanies.length >= companyData?.data?.count) {
        return;
      }
      setCompanyPaginationModel((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  };

  useEffect(() => {
    setCompanyPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [categoryData?.categoryID, setCompanyPaginationModel]);

  useEffect(() => {
    if (companyData?.data?.list) {
      if (companyPaginationModel.page === 0) {
        setAccumulatedCompanies(companyData.data.list);
      } else {
        setAccumulatedCompanies((prev) => {
          const existingIds = new Set(prev.map((c) => c.companyID));
          const newCompanies = companyData.data.list.filter((c) => !existingIds.has(c.companyID));
          return [...prev, ...newCompanies];
        });
      }
    }
  }, [companyData, companyPaginationModel.page]);

  const isCompanySectionDisabled =
    isRightPaneDisabled || selectedCategories?.length < 1 || isTenantBlocked;
  const isUserSectionDisabled =
    isRightPaneDisabled || !addNewCategory || isTenantBlocked;

  return (
    <Box
      sx={{
        height: { xs: "auto", lg: "80vh" },
        boxSizing: "border-box",
        cursor: isRightPaneDisabled ? "not-allowed" : "auto",
      }}
    >
      <Box
        sx={{
          height: { xs: "100%", lg: "100%" },
          display: "flex",
          flexDirection: "column",
          gap: { xs: 2.5, lg: "0px" },
          justifyContent: "space-between",
        }}
      >
        {/* SELECT COMPANY SECTION */}
        <SectionContainer
          id="tour-category-company-pane"
          disabled={isCompanySectionDisabled}
          sx={{
            height: { xs: "auto", lg: "48%" },
            minHeight: { xs: "260px", lg: "auto" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingLeft: "20px",
              paddingTop: "10px",
              paddingRight: "20px",
            }}
          >
            <StyledTypography style={{ padding: 0, margin: 0 }}>
              Copy Category to Companies
            </StyledTypography>
            {hasCompanyChanges && (
              <span style={{ color: "red", fontSize: 10, fontWeight: 600 }}>
                (Unsaved changes)
              </span>
            )}
          </div>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", px: "20px", pb: 1, fontSize: "13px" }}
          >
            Assign this category to other companies by checking them. Unchecking a company will remove the category.
          </Typography>
          {companyError && (
            <StyledTypography
              style={{ color: "red", paddingLeft: "20px", fontSize: "12px" }}
            >
              Please select at least one company to copy this category to
            </StyledTypography>
          )}
          {isRightPaneDisabled && (
            <StyledTypography
              style={{ color: "orange", paddingLeft: "20px", fontSize: "11px" }}
            >
              Complete the current operation first
            </StyledTypography>
          )}
          {selectedCategories?.length === 0 && !isRightPaneDisabled && (
            <StyledTypography
              style={{ color: "#666", paddingLeft: "20px", fontSize: "11px" }}
            >
              Select a category to manage company assignments
            </StyledTypography>
          )}
          <div
            onScroll={handleCompanyScroll}
            style={{
              flex: 1,
              overflowY: "auto",
              marginTop: 10,
            }}
          >
            {filteredCompany?.map((item, i) => {
              const isCurrent = item.companyID === currentCompany;
              const isChecked = companyForm?.some(
                (val) => String(val?.companyID) === String(item?.companyID),
              );
              const isDisabled = isCompanyCheckboxDisabled(item.companyID);
              const hasData = companiesWithCategory[item.companyID]?.hasData;
              const statusBadge = getCompanyStatus(item.companyID);

              return (
                <CompanyListBox
                  key={i}
                  hasdata={hasData ? 1 : 0}
                  isdisabled={isDisabled ? 1 : 0}
                  iscurrent={isCurrent ? 1 : 0}
                >
                  <FormControlLabel
                    control={
                      companiesWithCategory[item.companyID]?.exists ? (
                        <div style={{ width: 42, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <span style={{ color: 'green', fontWeight: 'bold', fontSize: '1.2rem' }}>✓</span>
                        </div>
                      ) : (
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCompanyCheckbox(item)}
                          disabled={isDisabled}
                          style={{
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        />
                      )
                    }
                    label={
                      <Tooltip title={item?.name} arrow placement="top-start">
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            display: "block",
                            width: "100%",
                            pr: 1,
                            fontSize: "15px",
                            fontWeight: 500
                          }}
                        >
                          {item?.name}
                        </Typography>
                      </Tooltip>
                    }
                    sx={{
                      cursor: isDisabled ? "not-allowed" : "pointer",
                      flex: 1,
                      minWidth: 0,
                      '& .MuiFormControlLabel-label': {
                        minWidth: 0,
                        overflow: 'hidden',
                      }
                    }}
                  />
                  {statusBadge}
                </CompanyListBox>
              );
            })}
            {isCompanyFetching && companyPaginationModel.page > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                <CircularProgress size={16} sx={{ color: "#7285b3" }} />
              </Box>
            )}
          </div>



          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: "20px",
              paddingBottom: "10px",
              gap: "10px",
            }}
          >
            <Button
              label="Update"
              onClick={submitCopyCategories}
              disabled={!hasCompanyChanges || isCompanySectionDisabled}
              variant="contained"
              size="small"
              sx={{
                height: 28,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Copy Category
            </Button>
            <Button
              label="Cancel"
              onClick={handleCancelCompanies}
              disabled={!hasCompanyChanges || isCompanySectionDisabled}
              variant="outlined"
              size="small"
              sx={{
                height: 28,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
          </div>
        </SectionContainer>

        {/* ADD USERS TO CATEGORY SECTION */}
        <SectionContainer
          id="tour-category-users-pane"
          disabled={isUserSectionDisabled}
          sx={{
            height: { xs: "auto", lg: "50%" },
            minHeight: { xs: "360px", lg: "auto" },
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ paddingLeft: 20, paddingTop: 10 }}>
            <StyledTypography>
              Add Users to Category {categoryData?.name}
            </StyledTypography>
            {isRightPaneDisabled && (
              <StyledTypography style={{ color: "orange", fontSize: "11px" }}>
                Complete the current operation first
              </StyledTypography>
            )}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 10,
                paddingRight: 20,
              }}
            >
              <CommonSearch
                style={{ flex: 1 }}
                label={"All Users"}
                value={searchUser}
                handleChange={(e) => setSearchUser(e.target.value)}
                disabled={isUserSectionDisabled}
              />

              <Tooltip
                title={sortOrder === "asc" ? "Sort Z-A" : "Sort A-Z"}
                placement="top"
              >
                <IconButton
                  onClick={handleSortToggle}
                  disabled={isUserSectionDisabled}
                  style={{
                    backgroundColor: isUserSectionDisabled
                      ? "#f5f5f5"
                      : "#e3f2fd",
                    borderRadius: 8,
                    padding: 8,
                    cursor: isUserSectionDisabled ? "not-allowed" : "pointer",
                  }}
                >
                  <SortByAlphaIcon
                    style={{
                      color: isUserSectionDisabled ? "#ccc" : IconColor,
                      fontSize: 22,
                    }}
                  />
                  {sortOrder === "asc" ? (
                    <ArrowUpwardIcon
                      style={{
                        color: isUserSectionDisabled ? "#ccc" : IconColor,
                        fontSize: 16,
                        marginLeft: -4,
                      }}
                    />
                  ) : (
                    <ArrowDownwardIcon
                      style={{
                        color: isUserSectionDisabled ? "#ccc" : IconColor,
                        fontSize: 16,
                        marginLeft: -4,
                      }}
                    />
                  )}
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div
            onScroll={handleScroll}
            style={{
              flex: 1,
              overflowY: "auto",
              marginTop: 10,
              position: "relative",
            }}
          >
            {isAllUsersFetching && userPaginationModel.page === 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(1px)",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={30} sx={{ color: "#7285b3" }} />
              </Box>
            )}
            {categoryData?.categoryID ? (
              accumulatedUsers?.length > 0 ? (
                <>
                  {accumulatedUsers.map((user, i) => (
                    <ListBox
                      key={i}
                      style={{
                        padding: "6px 12px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        backgroundColor: checkUserExistForCategory(user)
                          ? "#deedf5"
                          : "white",
                        borderRadius: 6,
                        cursor: "default",
                        opacity: isUserSectionDisabled ? 0.6 : 1,
                      }}
                    >
                      <IconButton
                        disabled={isUserSectionDisabled || checkUserExistForCategory(user) || addUserForCategorys.isPending}
                        onClick={() => addUserForCategory(user)}
                        style={{
                          padding: 8,
                        }}
                      >
                        {addUserForCategorys.isPending && addUserForCategorys.variables?.userID === user.userID ? (
                          <CircularProgress size={24} sx={{ color: IconColor }} />
                        ) : (
                          <GroupAddIcon
                            style={{
                              color: (isUserSectionDisabled || checkUserExistForCategory(user))
                                ? "#ccc"
                                : IconColor,
                              fontSize: 28,
                            }}
                          />
                        )}
                      </IconButton>
                      
                      <Typography style={{ flexGrow: 1, paddingLeft: 16, paddingRight: 16 }}>
                        {user?.username}
                        {user?.fullName ? ` (${user.fullName})` : ""}
                      </Typography>

                      <IconButton
                        disabled={isUserSectionDisabled || !checkUserExistForCategory(user) || removeUserFromCategory.isPending}
                        onClick={() => removeUser(user)}
                        style={{
                          padding: 8,
                        }}
                      >
                        {removeUserFromCategory.isPending && removeUserFromCategory.variables?.userId === user.userID ? (
                          <CircularProgress size={24} sx={{ color: IconColor }} />
                        ) : (
                          <PersonRemoveIcon
                            style={{
                              color: (isUserSectionDisabled || !checkUserExistForCategory(user))
                                ? "#ccc"
                                : IconColor,
                              fontSize: 28,
                            }}
                          />
                        )}
                      </IconButton>
                    </ListBox>
                  ))}
                  {isAllUsersFetching && userPaginationModel.page > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                      <CircularProgress size={16} sx={{ color: "#7285b3" }} />
                    </Box>
                  )}
                </>
              ) : (
                <StyledTypography style={{ textAlign: "center", marginTop: 20 }}>
                  No users match
                </StyledTypography>
              )
            ) : (
              <StyledTypography style={{ textAlign: "center" }}>
                Select any category
              </StyledTypography>
            )}
          </div>

        </SectionContainer>
      </Box>
    </Box>
  );
};
